import { pinnedContracts, productFlags } from '@hoodmorning/config';
import { SELECTED_ACCOUNT_CHAIN_ID, type Address, type Hex, type VaultIdentity } from '@hoodmorning/contracts';
import {
  encodePacked,
  getAddress,
  isAddressEqual,
  keccak256,
  toBytes,
  type PublicClient,
} from 'viem';

import { accountFactoryAbi, semiModularAccountAbi } from './abis.js';

export const VAULT_SALT_NAMESPACE = keccak256(
  toBytes('hoodmorning.product1.vault.v1'),
);
export const EXPECTED_SMA_B_ACCOUNT_ID = pinnedContracts.accountProfile.accountId;

export type SmaBVaultIntent<ChainId extends 4663 | 46630 = 4663> = {
  chainId: ChainId;
  owner: Address;
  vaultIndex: number;
  salt: Hex;
  accountMode: 'factory-deployed-sma-b';
  eip7702: false;
  factory: Address;
  implementation: Address;
  entryPoint: Address;
  sessionKeysEnabled: false;
};

export function deriveVaultSalt(vaultIndex: number): Hex {
  if (!Number.isSafeInteger(vaultIndex) || vaultIndex < 0) {
    throw new RangeError('vaultIndex must be a non-negative safe integer');
  }

  return keccak256(
    encodePacked(['bytes32', 'uint256'], [VAULT_SALT_NAMESPACE, BigInt(vaultIndex)]),
  );
}

/** Address derivation ignores userId; optional compatibility metadata is not authorization. */
export type VaultAddressIdentity<ChainId extends 4663 | 46630 = 4663> = Pick<VaultIdentity<ChainId>, 'owner' | 'chainId' | 'vaultIndex'> & { userId?: string };

export function createSmaBVaultIntent<ChainId extends 4663 | 46630>(identity: VaultAddressIdentity<ChainId>): SmaBVaultIntent<ChainId> {
  if (identity.chainId !== SELECTED_ACCOUNT_CHAIN_ID) {
    throw new Error(`Product 1 supports only the selected Robinhood Chain (${SELECTED_ACCOUNT_CHAIN_ID})`);
  }
  if (productFlags.sessionKeysEnabled !== false) {
    throw new Error('Product 1 refuses to construct a vault while session keys are enabled');
  }

  return {
    chainId: identity.chainId,
    owner: getAddress(identity.owner),
    vaultIndex: identity.vaultIndex,
    salt: deriveVaultSalt(identity.vaultIndex),
    accountMode: 'factory-deployed-sma-b',
    eip7702: false,
    factory: getAddress(pinnedContracts.accountFactory.address),
    implementation: getAddress(pinnedContracts.smaBImplementation.address),
    entryPoint: getAddress(pinnedContracts.entryPointV07.address),
    sessionKeysEnabled: false,
  };
}

export function assertDistinctVault(owner: Address, vault: Address): void {
  if (isAddressEqual(owner, vault)) {
    throw new Error('Refusing signer-address/EIP-7702 account: vault must be distinct from owner');
  }
}

type SmaBReadClient = Pick<PublicClient, 'getBytecode' | 'getChainId' | 'readContract'>;

export async function predictSmaBVault<ChainId extends 4663 | 46630>(
  client: SmaBReadClient,
  identity: VaultIdentity<ChainId>,
): Promise<Address> {
  const intent = createSmaBVaultIntent(identity);
  const chainId = await client.getChainId();
  if (chainId !== intent.chainId) {
    throw new Error(`RPC chain mismatch: expected ${intent.chainId}, received ${chainId}`);
  }

  const predicted = await client.readContract({
    address: intent.factory,
    abi: accountFactoryAbi,
    functionName: 'getAddressSemiModular',
    args: [intent.owner, BigInt(intent.salt)],
  });
  assertDistinctVault(intent.owner, predicted);
  return getAddress(predicted);
}

export type SmaBAccountPreflight = {
  valid: boolean;
  failures: string[];
  chainId: number;
  vault: Address;
  owner: Address | null;
  accountId: string | null;
  deployed: boolean;
};

export async function preflightSmaBAccount(
  client: SmaBReadClient,
  vault: Address,
  expectedOwner?: Address,
): Promise<SmaBAccountPreflight> {
  const failures: string[] = [];
  const chainId = await client.getChainId();
  if (chainId !== SELECTED_ACCOUNT_CHAIN_ID) failures.push(`CHAIN_ID_MISMATCH:${chainId}`);

  const code = await client.getBytecode({ address: vault });
  const deployed = code !== undefined && code !== '0x';
  if (!deployed) failures.push('VAULT_NOT_DEPLOYED');

  let accountId: string | null = null;
  let owner: Address | null = null;
  if (deployed) {
    try {
      accountId = await client.readContract({
        address: vault,
        abi: semiModularAccountAbi,
        functionName: 'accountId',
      });
      if (accountId !== EXPECTED_SMA_B_ACCOUNT_ID) {
        failures.push(`ACCOUNT_ID_MISMATCH:${accountId}`);
      }
    } catch {
      failures.push('ACCOUNT_ID_UNREADABLE');
    }

    try {
      const signerData = await client.readContract({
        address: vault,
        abi: semiModularAccountAbi,
        functionName: 'getFallbackSignerData',
      });
      owner = getAddress(signerData[0]);
      if (signerData[1]) failures.push('FALLBACK_SIGNER_DISABLED');
      if (expectedOwner !== undefined && !isAddressEqual(owner, expectedOwner)) {
        failures.push('OWNER_MISMATCH');
      }
      assertDistinctVault(owner, vault);
    } catch (error) {
      failures.push(
        error instanceof Error && error.message.includes('vault must be distinct')
          ? 'VAULT_EQUALS_OWNER'
          : 'OWNER_UNREADABLE',
      );
    }
  }

  return { valid: failures.length === 0, failures, chainId, vault, owner, accountId, deployed };
}
